import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

const FFS_REPORT_TYPE = 'ffs-lta-report' as RolodexEntryType

export interface UseFfsPartReportsResult {
  /**
   * Ids of the indications that have at least one saved `ffs-lta-report` for
   * the requested part (any level, any outcome -- a `Skipped` report counts).
   */
  indicationIdsWithReport: ReadonlySet<string>
  isLoading: boolean
  forceRefresh: () => Promise<unknown>
}

/**
 * Batch-checks, across many LTAs at once, which have a saved FFS report for a
 * given part -- the step-level counterpart to the per-indication
 * `useSavedReports`. Queries `ffs-lta-report` by `tags: { indication: [ids] }`
 * in a single request.
 *
 * Uses `useRolodexEntriesWithSyncedRefreshes` so the query participates in the
 * workspace's synced-refresh registry. NOTE: the drawer's own report saves go
 * through the plain `useRolodexEntries`-based `useSavedReports`, whose refresh
 * does NOT enter that registry, so callers must still drive `forceRefresh` here
 * off the report-mutation path (the FFS step passes it into the card's save
 * callbacks) -- the sync registry alone will not invalidate this query.
 */
export function useFfsPartReports(
  indicationIds: ReadonlyArray<string>,
  part: FfsPart
): UseFfsPartReportsResult {
  // Stable key so a fresh array reference each render doesn't re-issue the query.
  const idsKey = useMemo(
    () => [...indicationIds].sort().join(','),
    [indicationIds]
  )

  const query = useMemo(
    () =>
      idsKey
        ? { type: FFS_REPORT_TYPE, tags: { indication: idsKey.split(',') } }
        : undefined,
    [idsKey]
  )

  const { entries, isLoading, forceRefresh } =
    useRolodexEntriesWithSyncedRefreshes(query)

  const indicationIdsWithReport = useMemo(() => {
    const withReport = new Set<string>()
    for (const entry of entries as ReadonlyArray<GeckoEntry>) {
      const indicationId = entry.tags?.indication as string | undefined
      const ffsPart = (entry.data as { ffs_part?: string } | undefined)
        ?.ffs_part
      if (indicationId && ffsPart === part) {
        withReport.add(indicationId)
      }
    }
    return withReport
  }, [entries, part])

  return useMemo(
    () => ({ indicationIdsWithReport, isLoading, forceRefresh }),
    [indicationIdsWithReport, isLoading, forceRefresh]
  )
}
